import React, {FC, useCallback} from 'react';

import {defineMessage} from '../../../../libs/shared/helpers/src/internationalization/message';
import {HandlerOf, RendererOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {atIndex} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {DropdownProps} from '../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {VisualParticipantModel} from '../../../models/api/visualParticipantModel';
import {CalendarsPanelId} from '../../../store/panels/panelStateBase';
import {Localizer} from '../../../store/stateless/internationalizationSelectors';
import {VisualParticipantsDropdownBox} from '../editor/participantsEditor/visualParticipantsDropdownBox';

/*
 * Props.
 */

interface FindCalendarDropdownBoxProps {
  panelId: CalendarsPanelId;
  onAddressSelect: HandlerOf<string>;
  inputBackgroundColor?: string;
  placeholder?: string;
  noMargin?: boolean;
  width?: number;
  renderDefaultDropdown?: RendererOf<DropdownProps>;
}

/*
 * Component.
 */

export const FindCalendarDropdownBox: FC<FindCalendarDropdownBoxProps> = (props) => {
  const localize = useLocalize();
  const {panelId, onAddressSelect, inputBackgroundColor} = props;

  /*
   * Event handlers.
   */

  const onVisualParticipantsSelect = useCallback(
    (newVisualParticipants: ReadonlyArray<VisualParticipantModel>) => {
      const firstVisualParticipant = atIndex(newVisualParticipants, 0);
      if (!firstVisualParticipant) {
        return;
      }

      onAddressSelect(firstVisualParticipant.contact.handle);
    },
    [onAddressSelect],
  );

  /*
   * Render.
   */

  return (
    <VisualParticipantsDropdownBox
      panelId={panelId}
      onVisualParticipantsSelect={onVisualParticipantsSelect}
      hasExistingAttendees={false}
      placeholder={renderDropdownPlaceholder(props, localize)}
      isUnsavedDraft={false}
      inputBackgroundColor={inputBackgroundColor}
      noMargin={props.noMargin}
      width={props.width}
      renderDefaultDropdown={props.renderDefaultDropdown}
    />
  );
};

/*
 * Helpers.
 */

function renderDropdownPlaceholder(props: FindCalendarDropdownBoxProps, localize: Localizer) {
  if (props.placeholder) {
    return props.placeholder;
  }

  return localize(
    defineMessage({
      id: 'calendar-find-people',
      message: 'Find people',
    }),
  );
}
